import { useAuthStore } from '@/stores/auth'

export interface CaldavAccount {
  id: number
  name: string
  url: string
  readonly: boolean
  username: string | null
  has_password: boolean
}

export interface CaldavAccountInput {
  name: string
  url: string
  readonly: boolean
  username?: string | null
  password?: string | null
}

export interface TestConnectionResult {
  ok: boolean
  error?: string
}

// How a calendar keeps priority tags and PRIORITY equal: no tags, the three high/medium/low ones, the nine 1-9 ones, or both.
export type PriorityTagMode = 'off' | 'coarse' | 'fine' | 'both'

export interface CaldavCalendar {
  id: number
  caldav_account_id: number
  href: string
  name: string
  subscribed: boolean
  is_default: boolean
  priority_tag_mode: PriorityTagMode
}

export type SharePermissionType = 'everyone' | 'any_group' | 'specific_chat' | 'specific_user'

export interface CalendarSharePermission {
  id: number
  caldav_calendar_id: number
  permission_type: SharePermissionType
  target_chat_id: number | null
  target_telegram_user_id: number | null
  target_username: string | null
}

// A grant in the account-wide overview (`GET /me/share-permissions`), with the calendar it is on.
export interface CalendarShareGrant extends CalendarSharePermission {
  caldav_account_id: number
  calendar_name: string
  created_at: string
}

export interface CalendarSharePermissionInput {
  permission_type: SharePermissionType
  target_chat_id?: number | null
  target_username?: string | null
}

import type { BulletStyle } from '@/settings/listBullets'
import type { TagStyle } from '@/todos/hashtags'

export type MapProvider = 'osm' | 'geo' | 'apple' | 'apple_app' | 'google' | 'google_app'

// What the todo list page remembers (`users.list_view`); the search text is not part of it.
export interface ListViewPrefs {
  status: 'open' | 'all' | 'done'
  sort: 'manual' | 'due' | 'priority' | 'created' | 'title'
  group: 'none' | 'tag'
  collapsed_groups: string[] // (casefolded tag names of the folded groups)
}

export interface UserSettings {
  telegram_user_id: number
  username: string | null
  discoverable_by_username: boolean
  reveal_invite_failure_reason: boolean
  lightweight_confirmations: boolean
  verbose_sync_messages: boolean
  notify_new_todos: boolean
  // The map location links open, in todo descriptions and in bot messages (null = same as map_provider).
  map_provider: MapProvider
  map_provider_telegram: MapProvider | null
  // The hashtag spellings the user allows, in the order of their preference (at least one).
  tag_styles: TagStyle[]
  // List bullet rewriting in captured messages (settings/listBullets.ts).
  convert_list_bullets: boolean
  migrate_bullet_lists: boolean
  list_bullet_style: BulletStyle
  // The todo list page's last view, null = nothing stored.
  list_view: ListViewPrefs | null
  // The admin's global switch for the verbose sync messages, read-only here.
  verbose_sync_enabled: boolean
  // One of the supported languages, or null for automatic (Telegram client language).
  language_code: string | null
  // Export links given out before this moment no longer work.
  export_links_valid_since: string | null
  last_api_used_at: string | null
}

export interface BlockedUser {
  id: number
  blocked_telegram_user_id: number
  blocked_username: string | null
  created_at: string
}

export interface ChecklistPost {
  id: number
  title: string
  caldav_account_id: number
  caldav_calendar_href: string
  chat_id: number
  render_mode: 'native' | 'fallback'
  last_synced_at: string | null
  task_count: number
  completed_task_count: number
  account_name: string
  sort_category: 'position' | 'created' | 'due' | 'priority' | 'title' | string
  sort_direction: 'asc' | 'desc'
  filter_status: string
  filter_tag: string | null
  group_by: string
  page: number
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public conflictTagId?: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Sign-in itself is owned by the auth store (stores/auth.ts) and driven by the router guard
// (router/index.ts) before any page-level navigation completes -- by the time a page calls one
// of the functions below, the store's token is already populated.
async function request<R>(path: string, init?: RequestInit): Promise<R> {
  const auth = useAuthStore()

  const headers = new Headers(init?.headers)
  if (auth.token) headers.set('Authorization', `Bearer ${auth.token}`)
  // FormData bodies (uploads) need the browser's own multipart Content-Type with its boundary.
  if (init?.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(`/api${path}`, { ...init, headers })
  await throwIfFailed(response, auth)
  if (response.status === 204) return undefined as R
  return (await response.json()) as R
}

async function throwIfFailed(response: Response, auth: ReturnType<typeof useAuthStore>): Promise<void> {
  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = (await response.json()) as { detail?: string }
      if (body.detail) detail = body.detail
    } catch {
      // response wasn't JSON -- keep statusText
    }
    if (response.status === 401) {
      // JWT expired/invalid mid-session -- drop back to 'idle' so the next navigation's router
      // guard re-attempts sign-in and redirects to /auth if that fails too.
      auth.reset()
    }
    const conflict = Number(response.headers.get('X-Conflict-Tag-Id'))
    throw new ApiError(detail, response.status, Number.isInteger(conflict) && conflict > 0 ? conflict : undefined)
  }
}

export function listAccounts(): Promise<CaldavAccount[]> {
  return request('/accounts')
}

export function createAccount(input: CaldavAccountInput): Promise<CaldavAccount> {
  return request('/accounts', { method: 'POST', body: JSON.stringify(input) })
}

export function updateAccount(
  id: number,
  input: Partial<CaldavAccountInput>,
): Promise<CaldavAccount> {
  return request(`/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(input) })
}

export function deleteAccount(id: number): Promise<void> {
  return request(`/accounts/${id}`, { method: 'DELETE' })
}

export function testAccountConnection(id: number): Promise<TestConnectionResult> {
  return request(`/accounts/${id}/test-connection`, { method: 'POST' })
}

// Discovers (upserts) the account's calendars/todo-lists from the CalDAV server and returns all
// of them -- newly-seen ones default to subscribed=true.
export function listAccountCalendars(accountId: number): Promise<CaldavCalendar[]> {
  return request(`/accounts/${accountId}/calendars`)
}

export function setCalendarSubscribed(
  accountId: number,
  calendarId: number,
  subscribed: boolean,
): Promise<CaldavCalendar> {
  return request(`/accounts/${accountId}/calendars/${calendarId}`, {
    method: 'PATCH',
    body: JSON.stringify({ subscribed }),
  })
}

// Setting a calendar as default clears the flag on every other calendar the user owns (across all
// their accounts) server-side -- at most one default calendar per user at a time.
export function setCalendarDefault(
  accountId: number,
  calendarId: number,
  isDefault: boolean,
): Promise<CaldavCalendar> {
  return request(`/accounts/${accountId}/calendars/${calendarId}`, {
    method: 'PATCH',
    body: JSON.stringify({ is_default: isDefault }),
  })
}

// Keeps the priority tags of the chosen mode and the todos' PRIORITY equal in this calendar (for apps that can't set the priority).
export function setCalendarPriorityTags(
  accountId: number,
  calendarId: number,
  mode: PriorityTagMode,
): Promise<CaldavCalendar> {
  return request(`/accounts/${accountId}/calendars/${calendarId}`, {
    method: 'PATCH',
    body: JSON.stringify({ priority_tag_mode: mode }),
  })
}

export function listPosts(): Promise<ChecklistPost[]> {
  return request('/posts')
}

export function listCalendarSharePermissions(
  accountId: number,
  calendarId: number,
): Promise<CalendarSharePermission[]> {
  return request(`/accounts/${accountId}/calendars/${calendarId}/share-permissions`)
}

export function listShareGrants(): Promise<CalendarShareGrant[]> {
  return request('/me/share-permissions')
}

export function createCalendarSharePermission(
  accountId: number,
  calendarId: number,
  input: CalendarSharePermissionInput,
): Promise<CalendarSharePermission> {
  return request(`/accounts/${accountId}/calendars/${calendarId}/share-permissions`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function deleteCalendarSharePermission(
  accountId: number,
  calendarId: number,
  permissionId: number,
): Promise<void> {
  return request(`/accounts/${accountId}/calendars/${calendarId}/share-permissions/${permissionId}`, {
    method: 'DELETE',
  })
}

export function getMySettings(): Promise<UserSettings> {
  return request('/me')
}

export function updateMySettings(
  input: Partial<
    Pick<
      UserSettings,
      | 'discoverable_by_username'
      | 'reveal_invite_failure_reason'
      | 'lightweight_confirmations'
      | 'verbose_sync_messages'
      | 'notify_new_todos'
    >
  > & {
    // "" resets to automatic.
    language_code?: string
    // "" resets to the same as map_provider.
    map_provider?: MapProvider
    map_provider_telegram?: MapProvider | ''
    tag_styles?: TagStyle[]
    convert_list_bullets?: boolean
    migrate_bullet_lists?: boolean
    list_bullet_style?: BulletStyle
    list_view?: ListViewPrefs
    clear_list_view?: boolean
  },
): Promise<UserSettings> {
  return request('/me', { method: 'PATCH', body: JSON.stringify(input) })
}

export function listBlockedUsers(): Promise<BlockedUser[]> {
  return request('/me/blocked-users')
}

export function blockUser(username: string): Promise<BlockedUser> {
  return request('/me/blocked-users', { method: 'POST', body: JSON.stringify({ username }) })
}

export function unblockUser(id: number): Promise<void> {
  return request(`/me/blocked-users/${id}`, { method: 'DELETE' })
}

export const MCP_SCOPES = [
  { value: 'mcp_todos_read', label: 'Todos: read' },
  { value: 'mcp_todos_write', label: 'Todos: write' },
  { value: 'mcp_settings_user_read', label: 'User settings: read' },
  { value: 'mcp_settings_user_write', label: 'User settings: write' },
  { value: 'mcp_settings_tags_read', label: 'Tags: read' },
  { value: 'mcp_settings_tags_write', label: 'Tags: write' },
  { value: 'mcp_settings_calendar_read', label: 'Calendar config: read' },
  { value: 'mcp_settings_calendar_write', label: 'Calendar config: write' },
  { value: 'mcp_settings_calendar_password_read', label: 'Calendar password: read' },
  { value: 'mcp_settings_calendar_password_write', label: 'Calendar password: write' },
] as const

export interface McpToken {
  id: string
  name: string
  description: string | null
  scopes: string[]
  created_at: string
  expires_at: string | null
  revoked_at: string | null
  last_used_at: string | null
}

export interface McpTokenCreated extends McpToken {
  token: string
}

export interface McpTokenCreateInput {
  name: string
  description?: string | null
  scopes: string[]
  expires_at?: string | null
}

export function listMcpTokens(): Promise<McpToken[]> {
  return request('/tokens')
}

export function createMcpToken(input: McpTokenCreateInput): Promise<McpTokenCreated> {
  return request('/tokens', { method: 'POST', body: JSON.stringify(input) })
}

export function revokeMcpToken(id: string): Promise<McpToken> {
  return request(`/tokens/${id}/revoke`, { method: 'POST' })
}

export interface AdminCalendarAccount {
  id: number
  name: string
  url: string
  readonly: boolean
}

export interface AdminCalendarOwner {
  telegram_user_id: number
  username: string | null
}

export interface AdminCalendar {
  id: number
  name: string
  href: string
  subscribed: boolean
  last_seen_at: string
  account: AdminCalendarAccount
  owner: AdminCalendarOwner
}

export interface AdminCalendarList {
  items: AdminCalendar[]
  total: number
  page: number
  page_size: number
}

export interface AdminCalendarFilters {
  page?: number
  pageSize?: number
  q?: string
  ownerTelegramUserId?: number
  subscribed?: boolean
}

export function listAdminCalendars(filters: AdminCalendarFilters = {}): Promise<AdminCalendarList> {
  const params = new URLSearchParams()
  if (filters.page) params.set('page', String(filters.page))
  if (filters.pageSize) params.set('page_size', String(filters.pageSize))
  if (filters.q) params.set('q', filters.q)
  if (filters.ownerTelegramUserId != null) {
    params.set('owner_telegram_user_id', String(filters.ownerTelegramUserId))
  }
  if (filters.subscribed != null) params.set('subscribed', String(filters.subscribed))
  const qs = params.toString()
  return request(`/admin/calendars${qs ? `?${qs}` : ''}`)
}

export interface AdminSettings {
  verbose_sync_enabled: boolean
}

export function getAdminSettings(): Promise<AdminSettings> {
  return request('/admin/settings')
}

export function updateAdminSettings(input: Partial<AdminSettings>): Promise<AdminSettings> {
  return request('/admin/settings', { method: 'PATCH', body: JSON.stringify(input) })
}

export interface AdminBotInfo {
  bot_id: number
  username: string | null
  url: string | null
  has_custom_certificate: boolean | null
  pending_update_count: number | null
  ip_address: string | null
  last_error_date: number | null
  last_error_message: string | null
  last_synchronization_error_date: number | null
  max_connections: number | null
  allowed_updates: string[] | null
  raw_get_webhook_info: Record<string, unknown> | null
  photo_sha256: string | null
  photo_set_at: string | null
  fetched_at: string
}

export interface AdminBotCommand {
  command: string
  description: string
}

export interface AdminBotProfile {
  language_code: string
  scope: string
  name: string | null
  short_description: string | null
  description: string | null
  commands: AdminBotCommand[] | null
  raw_get_my_name: Record<string, unknown> | null
  raw_get_my_short_description: Record<string, unknown> | null
  raw_get_my_description: Record<string, unknown> | null
  raw_get_my_commands: AdminBotCommand[] | null
  fetched_at: string
}

export interface AdminBotRights {
  for_channels: boolean
  rights: Record<string, boolean | null>
  raw_get_my_default_administrator_rights: Record<string, unknown> | null
  fetched_at: string
}

export interface AdminBot {
  info: AdminBotInfo | null
  profiles: AdminBotProfile[]
  admin_rights: AdminBotRights[]
}

export function getAdminBot(): Promise<AdminBot> {
  return request('/admin/bot')
}

export interface DbDumpLink {
  url: string
  file_name: string
}

// A one-minute link to the whole database as a SQLite file, for Telegram's download popup (no session on it).
export function getDbDumpLink(): Promise<DbDumpLink> {
  return request('/admin/db-dump/link', { method: 'POST' })
}

export interface AdminMcpToken {
  owner: AdminCalendarOwner
  scopes: string[]
  created_at: string
  expires_at: string | null
  revoked_at: string | null
}

export function listAdminMcpTokens(): Promise<AdminMcpToken[]> {
  return request('/admin/mcp-tokens')
}

export interface VersionInfo {
  version: string
  git_commit: string
  git_branch: string
  build_time: string
  bot_username: string
}

export function getVersion(): Promise<VersionInfo> {
  return request('/version')
}

// # Todos tab # //

export interface TodoCalendar {
  id: number
  name: string
  href: string
  account_id: number
  account_name: string
  readonly: boolean
  is_default: boolean
  priority_tag_mode: PriorityTagMode
  cached_task_count: number
}

export type TodoStatus = 'NEEDS-ACTION' | 'IN-PROCESS' | 'COMPLETED' | 'CANCELLED'
export type TodoPrivacy = 'PUBLIC' | 'PRIVATE' | 'CONFIDENTIAL'
export type TodoEventStatus = 'TENTATIVE' | 'CONFIRMED' | 'CANCELLED'
export type TodoShowAs = 'OPAQUE' | 'TRANSPARENT'

export interface TodoAlarm {
  // An RFC 5545 duration relative to start/due ("-PT15M"), or an absolute ISO datetime.
  trigger: string
  related: 'START' | 'END' | null
  description: string | null
}

// RFC 5545 GEO: the exact point, next to the free text `location`.
export interface TodoGeo {
  latitude: number
  longitude: number
}

export interface TodoAttachment {
  index: number
  uri: string | null
  filename: string | null
  fmttype: string | null
  size: number | null
}

// Dates are ISO strings: "YYYY-MM-DD" for all-day values, full datetimes otherwise (with an
// offset, or without one for "floating" local times).
export interface Todo {
  uid: string
  summary: string
  description: string | null
  status: TodoStatus | null
  completed_at: string | null
  percent_complete: number | null
  priority: number
  start: string | null
  due: string | null
  categories: string[]
  url: string | null
  location: string | null
  geo: TodoGeo | null
  privacy: TodoPrivacy | null
  rrule: string | null
  alarms: TodoAlarm[]
  comments: string[]
  attachments: TodoAttachment[]
  assignees: string[]
  parent_uid: string | null
  sort_order: number | null
  flagged: boolean
  event_status: TodoEventStatus | null
  show_as: TodoShowAs | null
  created: string | null
  last_modified: string | null
}

export type TodoPatch = Partial<
  Pick<
    Todo,
    | 'summary'
    | 'description'
    | 'status'
    | 'percent_complete'
    | 'priority'
    | 'start'
    | 'due'
    | 'categories'
    | 'url'
    | 'location'
    | 'geo'
    | 'privacy'
    | 'rrule'
    | 'alarms'
    | 'comments'
    | 'assignees'
    | 'parent_uid'
    | 'sort_order'
    | 'flagged'
    | 'event_status'
    | 'show_as'
  >
>

function todoPath(calendarId: number, uid?: string, suffix = ''): string {
  const base = `/calendars/${calendarId}/todos`
  return uid === undefined ? base : `${base}/${encodeURIComponent(uid)}${suffix}`
}

export function listTodoCalendars(): Promise<TodoCalendar[]> {
  return request('/calendars')
}

export function listTodos(calendarId: number): Promise<{ calendar_id: number; todos: Todo[] }> {
  return request(todoPath(calendarId))
}

export function createTodo(calendarId: number, fields: TodoPatch & { summary: string }): Promise<Todo> {
  return request(todoPath(calendarId), { method: 'POST', body: JSON.stringify(fields) })
}

export function patchTodo(calendarId: number, uid: string, changes: TodoPatch): Promise<Todo> {
  return request(todoPath(calendarId, uid), { method: 'PATCH', body: JSON.stringify(changes) })
}

export function completeTodo(calendarId: number, uid: string, done: boolean): Promise<Todo> {
  return request(todoPath(calendarId, uid, '/complete'), { method: 'POST', body: JSON.stringify({ done }) })
}

export function deleteTodo(calendarId: number, uid: string, subtasks: 'delete' | 'orphan'): Promise<void> {
  return request(`${todoPath(calendarId, uid)}?subtasks=${subtasks}`, { method: 'DELETE' })
}

export function duplicateTodo(calendarId: number, uid: string): Promise<Todo> {
  return request(todoPath(calendarId, uid, '/duplicate'), { method: 'POST' })
}

export function moveTodo(calendarId: number, uid: string, targetCalendarId: number): Promise<Todo> {
  return request(todoPath(calendarId, uid, '/move'), {
    method: 'POST',
    body: JSON.stringify({ target_calendar_id: targetCalendarId }),
  })
}

export function addTodoComment(calendarId: number, uid: string, text: string): Promise<Todo> {
  return request(todoPath(calendarId, uid, '/comments'), { method: 'POST', body: JSON.stringify({ text }) })
}

export function addTodoLink(calendarId: number, uid: string, uri: string, filename?: string): Promise<Todo> {
  return request(todoPath(calendarId, uid, '/attachments/link'), {
    method: 'POST',
    body: JSON.stringify({ uri, filename: filename || null }),
  })
}

export function uploadTodoAttachment(calendarId: number, uid: string, file: File): Promise<Todo> {
  const form = new FormData()
  form.append('file', file)
  form.append('filename', file.name)
  return request(todoPath(calendarId, uid, '/attachments'), { method: 'POST', body: form })
}

export function removeTodoAttachment(calendarId: number, uid: string, index: number): Promise<Todo> {
  return request(todoPath(calendarId, uid, `/attachments/${index}`), { method: 'DELETE' })
}

export interface ExportLink {
  id: string
  label: string | null
  // The signed subscribe URL, works without the session (calendar apps).
  url: string
  created_at: string
  last_used_at: string | null
}

export interface ExportLinks {
  file_name: string
  // Signed link that works without the session (external browser, Telegram's download popup) and forces a download.
  download_url: string
  // The calendar's open subscription links, oldest first.
  links: ExportLink[]
}

export function getExportLinks(calendarId: number): Promise<ExportLinks> {
  return request(`/calendars/${calendarId}/export-links`)
}

export function createExportLink(calendarId: number, label: string | null): Promise<ExportLink> {
  return request(`/calendars/${calendarId}/export-links`, { method: 'POST', body: JSON.stringify({ label }) })
}

export function revokeExportLink(calendarId: number, linkId: string): Promise<void> {
  return request(`/calendars/${calendarId}/export-links/${linkId}`, { method: 'DELETE' })
}

// Invalidates every export link given out so far; returns the settings with the new valid-since date.
export function revokeExportLinks(): Promise<UserSettings> {
  return request('/me/export-links/revoke', { method: 'POST' })
}

// Inline (base64) attachments need the session's bearer token, so they're fetched as a blob
// instead of being linked to directly.
export async function fetchInlineAttachment(calendarId: number, uid: string, index: number): Promise<Blob> {
  const auth = useAuthStore()
  const headers = new Headers()
  if (auth.token) headers.set('Authorization', `Bearer ${auth.token}`)
  const response = await fetch(`/api${todoPath(calendarId, uid, `/attachments/${index}`)}?dl=1`, { headers })
  await throwIfFailed(response, auth)
  return await response.blob()
}

export interface TagAlias {
  id: number
  alias: string
}

export interface TagImplication {
  id: number
  // The id of the added tag (what removing takes) and its name.
  tag_id: number
  name: string
}

// Where a tag is also written into the todo's text as a hashtag: in front of / behind the title, as the first / last line of the description.
export interface TagTextFlags {
  text_title_start: boolean
  text_title_end: boolean
  text_body_start: boolean
  text_body_end: boolean
}

export interface Tag extends TagTextFlags {
  id: number
  name: string
  // `#rrggbb`, or none.
  color: string | null
  // Moved to the front of a todo's tags on every pull, so it counts as the category.
  is_category: boolean
  // Never the first tag of a todo (not its category) while the todo has another one.
  is_additional: boolean
  // Never used as the group of a todo in task lists.
  ignore_for_grouping: boolean
  // Ordered as far back as possible in a todo's tags.
  keep_last: boolean
  // A built-in tag (the subtask tag): renamable, but no aliases and not deletable.
  internal: boolean
  // Other writings of the tag, turned into its name on every pull.
  aliases: TagAlias[]
  // The tags added to every todo that has this one, on every pull.
  implies: TagImplication[]
  // After a rename with the `migrate` mode: how many todos were rewritten.
  migrated_todos: number
  // After a rename with the `migrate` mode: the background job rewriting the todos, see `getMigrationJob`.
  migration_job_id: string | null
}

export interface MigrationJob {
  state: 'running' | 'done' | 'failed'
  done: number
  total: number
  migrated: number
  error: string | null
}

export type RenameMode = 'alias' | 'migrate' | 'none'

export function listTags(): Promise<Tag[]> {
  return request('/tags')
}

export function createTag(name: string, color: string | null = null): Promise<Tag> {
  return request('/tags', { method: 'POST', body: JSON.stringify({ name, color }) })
}

export function updateTag(
  id: number,
  input: { name?: string; rename_mode?: RenameMode; color?: string | null; is_category?: boolean; is_additional?: boolean; ignore_for_grouping?: boolean; keep_last?: boolean } & Partial<TagTextFlags>,
): Promise<Tag> {
  return request(`/tags/${id}`, { method: 'PATCH', body: JSON.stringify(input) })
}

export function getMigrationJob(id: string): Promise<MigrationJob> {
  return request(`/tags/migration-jobs/${encodeURIComponent(id)}`)
}

export function deleteTag(id: number): Promise<void> {
  return request(`/tags/${id}`, { method: 'DELETE' })
}

export function addTagAlias(id: number, alias: string): Promise<Tag> {
  return request(`/tags/${id}/aliases`, { method: 'POST', body: JSON.stringify({ alias }) })
}

export function removeTagAlias(id: number, aliasId: number): Promise<Tag> {
  return request(`/tags/${id}/aliases/${aliasId}`, { method: 'DELETE' })
}

// `name` is a tag or an alias of one; a name that is no tag yet becomes one.
export function addTagImplication(id: number, name: string): Promise<Tag> {
  return request(`/tags/${id}/implies`, { method: 'POST', body: JSON.stringify({ name }) })
}

export function removeTagImplication(id: number, impliedTagId: number): Promise<Tag> {
  return request(`/tags/${id}/implies/${impliedTagId}`, { method: 'DELETE' })
}
